import {
  endOfToday,
  format,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns'
import { ArrowDownRight, ArrowUpRight, BarChart3 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { type AnalyticsParams, useListingAnalytics } from '~/entities/stats'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { cn } from '~/shared/lib/utils'
import { METRIC_COLORS } from '~/shared/styles/chart-palette'
import { Card } from '~/shared/ui/card'
import { Disclosure } from '~/shared/ui/disclosure'
import { Skeleton } from '~/shared/ui/skeleton'
import { StackedBars, type StackedBarsSegment } from '~/shared/ui/stacked-bars/stacked-bars'
import type { StackedBarsPoint } from '~/shared/ui/stacked-bars/stacked-bars-data'

type Granularity = 'day' | 'week' | 'month' | 'all'
type ResolvedGranularity = 'day' | 'week' | 'month'
type MetricKey = 'views' | 'interactions' | 'follows' | 'impressions'

const METRIC_KEYS: MetricKey[] = ['views', 'impressions', 'interactions', 'follows']
const EMPTY_TOTALS: Record<MetricKey, number> = {
  views: 0,
  interactions: 0,
  follows: 0,
  impressions: 0,
}

function metricLabel(key: MetricKey): string {
  if (key === 'views') return m.views()
  if (key === 'interactions') return m.interactions()
  if (key === 'impressions') return m.impressions()
  return m.follows()
}

/** Current + previous equal-length windows for a granularity, as YYYY-MM-DD. */
function windowsFor(granularity: ResolvedGranularity) {
  const today = endOfToday()
  const iso = (d: Date) => format(d, 'yyyy-MM-dd')
  if (granularity === 'week') {
    const curFrom = startOfWeek(subWeeks(today, 11), { weekStartsOn: 1 })
    const prevTo = subDays(curFrom, 1)
    return {
      cur: { from: iso(curFrom), to: iso(today) },
      prev: { from: iso(subWeeks(curFrom, 12)), to: iso(prevTo) },
    }
  }
  if (granularity === 'month') {
    const curFrom = startOfMonth(subMonths(today, 11))
    const prevTo = subDays(curFrom, 1)
    return {
      cur: { from: iso(curFrom), to: iso(today) },
      prev: { from: iso(subMonths(curFrom, 12)), to: iso(prevTo) },
    }
  }
  const curFrom = subDays(today, 29)
  const prevTo = subDays(curFrom, 1)
  return {
    cur: { from: iso(curFrom), to: iso(today) },
    prev: { from: iso(subDays(prevTo, 29)), to: iso(prevTo) },
  }
}

function chartSegments(): StackedBarsSegment<MetricKey>[] {
  return (['views', 'interactions', 'follows', 'impressions'] as const).map((key) => ({
    key,
    label: metricLabel(key),
    color: METRIC_COLORS[key],
  }))
}

function granularityOptions(): { value: Granularity; label: string }[] {
  return [
    { value: 'day', label: m.chartPeriodDay() },
    { value: 'week', label: m.chartPeriodWeek() },
    { value: 'month', label: m.chartPeriodMonth() },
    { value: 'all', label: m.chartPeriodAll() },
  ]
}

function deltaPct(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null
  return ((current - previous) / previous) * 100
}

function ListingAnalyticsChart({ listingId }: { listingId: string }) {
  const { data: business } = useCurrentBusiness()
  const locale = getLocale()
  const [granularity, setGranularity] = useState<Granularity>('all')

  const businessId = business?.id ?? ''
  const isAll = granularity === 'all'
  const win = useMemo(() => (granularity === 'all' ? null : windowsFor(granularity)), [granularity])
  const curParams: AnalyticsParams = win ? { ...win.cur, granularity } : { granularity: 'all' }
  const prevParams: AnalyticsParams = win ? { ...win.prev, granularity } : { granularity: 'all' }
  const cur = useListingAnalytics(businessId, listingId, curParams)
  const prev = useListingAnalytics(businessId, listingId, prevParams, {
    enabled: !isAll,
  })

  const series: StackedBarsPoint<MetricKey>[] = (cur.data?.series ?? []).map((bucket) => ({
    date: bucket.bucket,
    values: {
      views: bucket.views,
      interactions: bucket.interactions,
      follows: bucket.follows,
      impressions: bucket.impressions,
    },
  }))

  const loading = cur.isLoading || !business
  const totals = cur.data?.totals ?? EMPTY_TOTALS
  const prevTotals = prev.data?.totals ?? EMPTY_TOTALS
  const viewsChange =
    isAll || prevTotals.views === 0 ? null : (totals.views - prevTotals.views) / prevTotals.views

  return (
    <div className="@container space-y-4">
      <StackedBars
        series={series}
        segments={chartSegments()}
        bucket={cur.data?.granularity ?? 'day'}
        title={m.listingAnalytics()}
        icon={<BarChart3 className="h-4 w-4 text-muted-foreground" />}
        headline={{
          value: totals.views,
          label: m.views(),
          change: viewsChange,
          changeLabel: m.vsPreviousPeriod(),
        }}
        periods={granularityOptions()}
        period={granularity}
        onPeriodChange={setGranularity}
        loading={loading}
        emptyText={m.noAnalyticsDataAvailable()}
        height={200}
        legend
        locale={locale}
      />

      {!loading && !isAll && (
        <div className="grid grid-cols-2 gap-3 @2xl:grid-cols-4">
          {METRIC_KEYS.map((key) => (
            <StatTile
              key={key}
              label={metricLabel(key)}
              value={totals[key]}
              previous={prevTotals[key]}
              color={METRIC_COLORS[key]}
              showDelta={!isAll}
              locale={locale}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function ListingNumbersCard({ listingId }: { listingId: string }) {
  const { data: business } = useCurrentBusiness()
  const locale = getLocale()
  const all = useListingAnalytics(business?.id ?? '', listingId, { granularity: 'all' })
  const totals = all.data?.totals ?? EMPTY_TOTALS

  return (
    <Card className="space-y-4 p-4 sm:p-6">
      <h2 className="flex items-center gap-2 font-semibold font-serif text-lg">
        <BarChart3 className="size-5" />
        {m.numbersTitle()}
      </h2>
      {all.isLoading || !business ? (
        <Skeleton className="h-20 w-full rounded-lg" />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {METRIC_KEYS.map((key) => (
            <div key={key} className="rounded-lg bg-muted px-4 py-3">
              <p className="font-semibold font-serif text-2xl tabular-nums">
                {totals[key].toLocaleString(locale)}
              </p>
              <p className="truncate text-muted-foreground text-sm">{metricLabel(key)}</p>
            </div>
          ))}
        </div>
      )}
      <div className="border-t pt-2">
        <Disclosure title={m.numbersAll()} contentClassName="pb-2">
          <ListingAnalyticsChart listingId={listingId} />
        </Disclosure>
      </div>
    </Card>
  )
}

function StatTile({
  label,
  value,
  previous,
  color,
  showDelta,
  locale,
}: {
  label: string
  value: number
  previous: number
  color: string
  showDelta: boolean
  locale: string
}) {
  const pct = showDelta ? deltaPct(value, previous) : null
  const up = pct !== null && pct >= 0
  return (
    <div className="rounded-lg border p-3">
      <div className="flex items-center gap-1.5">
        <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
        <span className="truncate text-muted-foreground text-xs">{label}</span>
      </div>
      <p className="mt-1 font-serif font-semibold text-2xl tabular-nums">
        {value.toLocaleString(locale)}
      </p>
      {pct !== null && Math.round(pct) !== 0 && (
        <span
          className={cn(
            'mt-0.5 inline-flex items-center gap-0.5 text-xs tabular-nums',
            up ? 'text-emerald-600 dark:text-emerald-500' : 'text-rose-600 dark:text-rose-500',
          )}
        >
          {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
          {Math.abs(Math.round(pct))}%
        </span>
      )}
    </div>
  )
}
